import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { RxTemplate } from '@shared/rx-templates/rx-template.model';
import { ProcedureMap } from '@shared/models/procedure-map';
import { ISectionDirtyCheckService } from '@shared/models/section-dirty-check';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { DentureDetailsState } from './state/denture-details.store';
import { DentureDetailsQuery } from './state/denture-details.query';
import { DentureBaseFieldsVisibility, DentureDetailsVisibilityService } from './denture-details-visibility.service';

@Injectable({ providedIn: 'root' })
export class DentureDetailsDirtyCheckService implements ISectionDirtyCheckService {
	/**
	 * Checks whether any base denture field (stage, mould, shadeSystem, teethShade, gingival)
	 * has user data that would be lost when it becomes hidden in the target template.
	 *
	 * `defaultShadeSystemId` distinguishes an auto-filled shade from a user-modified one —
	 * if the current value matches the default it is not treated as user data.
	 */
	static checkBaseFields(
		tgt: DentureBaseFieldsVisibility,
		cur: DentureBaseFieldsVisibility,
		s: DentureDetailsState,
		defaultShadeSystemId: number | undefined
	): boolean {
		const shadeSystemIsUserModified = s.shadeSystem != null && s.shadeSystem.Id !== defaultShadeSystemId;

		return (
			(cur.stage && !tgt.stage && s.stage != null) ||
			(cur.mould && !tgt.mould && s.mould != null) ||
			(cur.shadeSystem && !tgt.shadeSystem && shadeSystemIsUserModified) ||
			(cur.teethShade && !tgt.teethShade && s.teethShade != null) ||
			(cur.gingival && !tgt.gingival && s.gingivalShade != null)
		);
	}

	constructor(
		private readonly query: DentureDetailsQuery,
		private readonly visibilityService: DentureDetailsVisibilityService,
		private readonly shellQuery: ShellQuery
	) {}

	willLoseDataFor$(targetTemplate: Readonly<RxTemplate>, targetProcedureMap: ProcedureMap | null): Observable<boolean> {
		return this.visibilityService.fieldsFor$(targetTemplate, targetProcedureMap).pipe(
			withLatestFrom(this.visibilityService.fields$, this.visibilityService.isSectionVisible$),
			map(([tgt, cur, isVisible]) => {
				if (!isVisible) {
					return false;
				}

				return DentureDetailsDirtyCheckService.checkBaseFields(
					tgt,
					cur,
					this.query.getValue(),
					this.shellQuery.defaultShadeSystemId
				);
			})
		);
	}
}
